import { Modal } from '@atoms/index';
import VerifyOtpModal from '@components/Auth/VerifyOtpModal';
import VerifyPhoneAndEmailModal from '@components/Auth/VerifyPhoneAndEmailModal';
import { HeadTagData } from '@helpers/headTagData';
import NextHeadTag from '@ui/molecules/NextHeadTag';
import { useEffect, useRef, useState } from 'react';
import LoginModalData from '@components/Auth/LoginModal/LoginModalData';
import { createSession } from '@services/session.service';
import MessageModal from '@molecules/MessageModal';
import { useRouter } from 'next/router';
import { useInvestorContext } from '@context/InvestorContextProvider';
import { otpContextEnum } from '@ui/helpers/enum';
import texts from '@components/Auth/en.json';
import loginModalTexts from '@components/Auth/LoginModal/LoginModalData/en.json';
import { triggerCustomEvent } from '@helpers/miscelleanous';
import { gaEventNameEnum, taxOptimiserV2Events } from '@helpers/enums/gaEventNameEnum';
import {
    logEvent,
    setUserData,
    moengageEventConstants,
    setUserAttribute,
    moengageAttributesConstants,
} from '@services/moengage';
import { taxOptimiserBrandName } from '@components/TaxOptimiser/helpers/constants';
import useWindowDimensions from '@hooks/useWindowDimensions';

function LoginModal({
    show,
    onClose,
    isFeatureSpecificLogin = false,
    featureBrandName = null,
    initialShowEmailInput = false,
}) {
    const [showVerifyOtpModal, setShowVerifyOtpModal] = useState(false);
    const [userInput, setUserInput] = useState({});
    const [loading, setLoading] = useState(false);
    const [multipleLogin, setMultipleLogin] = useState(false);
    const [isSessionCreationError, setIsSessionCreationError] = useState(false);
    const router = useRouter();
    const { setMenus } = useInvestorContext();
    const mobileRef = useRef();
    const emailRef = useRef();
    const [focusMobile, setFocusMobile] = useState(true);
    const [focusEmail, setFocusEmail] = useState(false);
    const [otpModes, setOtpModes] = useState([]);
    const [isModalInLoadingState, setIsModalInLoadingState] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(initialShowEmailInput);
    const { width } = useWindowDimensions();

    const isTaxOptimiserLogin =
        isFeatureSpecificLogin && featureBrandName === taxOptimiserBrandName;

    const editMobileNumber = () => {
        setShowVerifyOtpModal(false);
        setFocusMobile(true);
    };

    const editEmail = () => {
        setShowVerifyOtpModal(false);
        setFocusEmail(true);
    };

    useEffect(() => {
        if (focusEmail && emailRef.current) {
            emailRef.current.focus();
            setFocusEmail(false);
        }
    }, [emailRef, focusEmail]);

    useEffect(() => {
        if (focusMobile && mobileRef.current) {
            mobileRef.current?.numberInputRef?.focus();
            setFocusMobile(false);
        }
    }, [mobileRef, focusMobile]);

    const onBack = () => {
        setIsSessionCreationError(false);
        setShowVerifyOtpModal(false);
    };

    // NRE/NRO signup only: Indian signups go through VerifyOtpModal -> /email-or-google-signup.
    const onNriOtpVerificationSuccess = async ({ otpIdList }) => {
        const gclid = router?.query?.gclid;
        const response = await createSession({
            mobile: userInput.mobile,
            otpIdList,
            userDetails: { ...userInput, countryCode: userInput.countryCodeWithPrefix },
            gclid,
            router,
            setMenus
        });
        if (response.status) {
            await setUserData({ auth: response?.entity });
            logEvent({
                eventName: moengageEventConstants.SIGNUP_SUCCESS,
            });
            setUserAttribute({
                attributeName: moengageAttributesConstants.WHATSAPP_OPT_IN,
                attributeValue: userInput?.whatsappOptIn,
            });
            triggerCustomEvent(gaEventNameEnum.SIGNUP_VERIFY_OTP, { mobile: userInput.mobile });
            if (isTaxOptimiserLogin) {
                triggerCustomEvent(taxOptimiserV2Events.TO_OTP_VERIFIED_SIGNUP, { mobile: userInput.mobile });
            }
        }
        setIsSessionCreationError(!response.status);
    };

    return (
        <>
            <NextHeadTag
                {...HeadTagData?.[
                    isTaxOptimiserLogin
                        ? '#taxOptimiserLogin'
                        : '#loginModal'
                ]}
            />

            {show && !showVerifyOtpModal && (
                <Modal
                    show={show}
                    onClose={isModalInLoadingState ? null : onClose}
                    onCloseButtonMouseDown={e => e.preventDefault()}
                    width="max-w-[440px]"
                    height="max-h-[98%]"
                    className="thin-scrollbar"
                    animation="popIn"
                    labelBorderClass="border-none"
                    labelPadding="px-4 md:px-6 pt-4"
                    labelParaClass={`${
                        isTaxOptimiserLogin
                            ? 'text-basicWhite h6-semibold sm:h5-semibold'
                            : 'text-primary-500 h5-semibold'
                        }
                    `}
                    isDarkTheme={isTaxOptimiserLogin}
                    label={
                        showEmailInput
                            ? loginModalTexts.SignUp
                            : isTaxOptimiserLogin
                                ? `Login to ${taxOptimiserBrandName}`
                                : loginModalTexts.Login
                    }
                    paddingClass="p-4 md:px-6 md:pt-4 md:pb-6"
                    roundedClass="rounded-2xl"
                    labelSize={' '}
                    modalColorClass={
                        isTaxOptimiserLogin
                            ? 'text-basicWhite bg-basicBlack border border-gray-500'
                            : 'text-basicBlack bg-basicWhite'
                    }
                    mainWrapperClassName={
                        isTaxOptimiserLogin ? 'backdrop-blur-sm inset-0' : ''
                    }
                    children={
                        <LoginModalData
                            userInput={userInput}
                            setUserInput={setUserInput}
                            setShowVerifyOtpModal={(value) =>
                                setShowVerifyOtpModal(value)
                            }
                            loading={loading}
                            setLoading={setLoading}
                            onClose={onClose}
                            setMultipleLogin={setMultipleLogin}
                            mobileRef={mobileRef}
                            emailRef={emailRef}
                            setOtpModes={setOtpModes}
                            setModalLoadingState={setIsModalInLoadingState}
                            isDarkTheme={isTaxOptimiserLogin}
                            isFeatureLogIn={isTaxOptimiserLogin}
                            isTaxOptimiserLogin={isTaxOptimiserLogin}
                            showEmailInput={showEmailInput}
                            setShowEmailInput={setShowEmailInput}
                        />
                    }
                />
            )}

            {showVerifyOtpModal && !isSessionCreationError && userInput.isNriSignup ? (
                <VerifyPhoneAndEmailModal
                    show={showVerifyOtpModal}
                    title={texts?.VerifyDetails}
                    mobileContext={otpContextEnum.MOBILE_VERIFICATION}
                    onClose={onClose}
                    mobileNumber={userInput.mobile}
                    emailId={userInput.email}
                    editMobileNumber={editMobileNumber}
                    editEmailId={editEmail}
                    countryCode={userInput.countryCode}
                    onSuccess={onNriOtpVerificationSuccess}
                    isDarkTheme={isTaxOptimiserLogin}
                    isTaxOptimiserLogin={isTaxOptimiserLogin}
                />
            ) : null}

            {showVerifyOtpModal && !isSessionCreationError && !userInput.isNriSignup ? (
                <VerifyOtpModal
                    show={showVerifyOtpModal}
                    onClose={() => {
                        onClose();
                    }}
                    mobileNumber={userInput.mobile}
                    editMobileNumber={editMobileNumber}
                    countryCode={userInput.countryCode}
                    userInput={userInput}
                    multipleLogin={multipleLogin}
                    otpModes={otpModes}
                    onSessionCreationError={() => setIsSessionCreationError(true)}
                    isTaxOptimiserLogin={isTaxOptimiserLogin}
                />
            ) : null}

            {isSessionCreationError &&
                <MessageModal
                    isError={isSessionCreationError}
                    show={isSessionCreationError}
                    title={userInput.isNriSignup ? texts?.VerifyOTP : texts?.UnableToLogin}
                    buttonText={texts?.TryAgain}
                    message={userInput.isNriSignup ? texts?.UnableToSignUp : texts?.ErrorLoggingIn}
                    onClick={onBack}
                    onClose={onClose}
                    isTaxOptimiserLogin={isTaxOptimiserLogin}
                />}
        </>
    );
}

export default LoginModal;
